import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ComponentRef,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    Type,
    ViewChild,
    ViewContainerRef,
    inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { ChatMessage, ChatTranscribingPayload } from '../../../types/chat-session.types';
import { ChatMessageViewConfig, IChatTemplate } from '../../../types/chat-template.types';
import { MarkdownPipe } from '../../../pipes/markdown.pipe';

@Component({
    selector: 'chat-message',
    standalone: true,
    imports: [CommonModule, MarkdownPipe],
    templateUrl: './chat-message.component.html',
    styleUrls: ['./chat-message.component.scss'],
})
export class ChatMessageComponent implements AfterViewInit, OnChanges, OnDestroy {
    @Input() public message!: ChatMessage;
    @Input() public template!: IChatTemplate;
    /** Позволяет переопределить viewConfig без изменения template (например, для activity-пузырька). */
    @Input() public viewConfigOverride: Partial<ChatMessageViewConfig> | null = null;

    @ViewChild('blockHost', { read: ViewContainerRef }) private blockHost?: ViewContainerRef;

    private readonly cdr = inject(ChangeDetectorRef);
    private blockRef: ComponentRef<{ message: ChatMessage }> | null = null;
    private viewReady = false;

    public get isTranscribing(): boolean {
        const payload = this.message?.payload as ChatTranscribingPayload | undefined;
        return Boolean(payload?.transcribing);
    }

    public get viewConfig(): Required<ChatMessageViewConfig> {
        const cfg = this.viewConfigOverride ?? this.template.config.chatMessageViewConfig ?? {};
        const hideMeta = this.isTranscribing;

        return {
            showAvatar: cfg.showAvatar ?? true,
            showSenderName: cfg.showSenderName ?? true,
            showDateTime: hideMeta ? false : (cfg.showDateTime ?? true),
            showDeliveryStatus: hideMeta ? false : (cfg.showDeliveryStatus ?? true),
            showLoadingSpinner: this.isTranscribing || (cfg.showLoadingSpinner ?? false),
        };
    }

    public get blockComponent(): Type<unknown> | null {
        if (this.message.type === 'text') {
            return null;
        }

        try {
            return this.template.getComponent(this.message.type);
        } catch {
            return null;
        }
    }

    public ngAfterViewInit(): void {
        this.viewReady = true;
        this.renderBlock();
        this.cdr.detectChanges();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (!this.viewReady) {
            return;
        }

        if (changes['message'] || changes['template']) {
            this.renderBlock();
        }
    }

    public ngOnDestroy(): void {
        this.clearBlock();
    }

    private renderBlock(): void {
        const component = this.blockComponent;

        if (!component || !this.blockHost) {
            this.clearBlock();
            return;
        }

        if (this.blockRef?.componentType === component) {
            this.blockRef.setInput('message', this.message);
            return;
        }

        this.clearBlock();
        this.blockRef = this.blockHost.createComponent(component) as ComponentRef<{ message: ChatMessage }>;
        this.blockRef.setInput('message', this.message);
    }

    private clearBlock(): void {
        this.blockRef?.destroy();
        this.blockRef = null;
        this.blockHost?.clear();
    }
}
